"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Platform = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Platform"));
var _GutterContext = _interopRequireDefault(require("../providers/GutterContext"));
var _helpers = _interopRequireDefault(require("../helpers"));
var _utils = require("../../utils");
var _ViewportProvider = require("../../ViewportProvider");
var _ThemeProvider = require("../../ThemeProvider");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const Col = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    horizontalAlign,
    children,
    xs,
    sm,
    md,
    lg,
    xl,
    xsOffset,
    smOffset,
    mdOffset,
    lgOffset,
    xlOffset,
    flex,
    ...viewProps
  } = _ref;
  const gutter = _react.default.useContext(_GutterContext.default);
  const viewport = (0, _ViewportProvider.useViewport)();
  const {
    themeOptions: {
      enableMediaQueryStyleSheet
    }
  } = (0, _ThemeProvider.useTheme)();
  const hiddenLevels = (0, _helpers.default)([xs, sm, md, lg, xl]);
  const getHorizontalAlignLevel = () => {
    if (typeof horizontalAlign === 'object') {
      return (0, _helpers.default)([horizontalAlign.xs, horizontalAlign.sm, horizontalAlign.md, horizontalAlign.lg, horizontalAlign.xl]);
    }
    if (typeof horizontalAlign === 'string') {
      return [horizontalAlign, horizontalAlign, horizontalAlign, horizontalAlign, horizontalAlign];
    }
    return ['inherit', 'inherit', 'inherit', 'inherit', 'inherit'];
  };
  const horizontalAlignLevel = getHorizontalAlignLevel();
  const toPercent = num => {
    return `${num / 12 * 100}%`;
  };
  const calculateWidth = value => {
    if (typeof value === 'undefined') {
      return {};
    }
    if (typeof value === 'number') {
      const percent = toPercent(value);
      return {
        maxWidth: percent,
        flexBasis: percent
      };
    }
    return {
      flexGrow: 1,
      flexBasis: 0,
      maxWidth: '100%'
    };
  };
  const calculateOffset = value => {
    if (typeof value === 'number') {
      const percent = toPercent(value);
      return {
        marginLeft: percent
      };
    }
    return {};
  };

  // TODO: consider setting this to always 'flex' in a major release.
  // `display: block` is invalid in native apps.
  // See https://telusdigital.atlassian.net/browse/UDS1-92
  const shown = !flex && _Platform.default.OS === 'web' ? 'block' : 'flex';
  const sizesArray = [xs, sm, md, lg, xl];
  const offSetsArray = [xsOffset, smOffset, mdOffset, lgOffset, xlOffset];
  const sizesWithIheritance = (0, _helpers.default)(sizesArray);
  const offsetsWithIheritance = (0, _helpers.default)(offSetsArray);
  const sizes = {
    xs: sizesWithIheritance[0],
    sm: sizesWithIheritance[1],
    md: sizesWithIheritance[2],
    lg: sizesWithIheritance[3],
    xl: sizesWithIheritance[4]
  };
  const offsets = {
    xs: offsetsWithIheritance[0],
    sm: offsetsWithIheritance[1],
    md: offsetsWithIheritance[2],
    lg: offsetsWithIheritance[3],
    xl: offsetsWithIheritance[4]
  };
  const staticStyles = {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    maxWidth: '100%',
    paddingLeft: gutter ? 16 : 0,
    paddingRight: gutter ? 16 : 0
  };
  const stylesByViewport = {
    xs: {
      display: hiddenLevels[0] === 0 ? 'none' : shown,
      textAlign: horizontalAlignLevel[0],
      ...calculateWidth(sizes.xs),
      ...calculateOffset(offsets.xs)
    },
    sm: {
      display: hiddenLevels[1] === 0 ? 'none' : shown,
      textAlign: horizontalAlignLevel[1],
      ...calculateWidth(sizes.sm),
      ...calculateOffset(offsets.sm)
    },
    md: {
      display: hiddenLevels[2] === 0 ? 'none' : shown,
      textAlign: horizontalAlignLevel[2],
      ...calculateWidth(sizes.md),
      ...calculateOffset(offsets.md)
    },
    lg: {
      display: hiddenLevels[3] === 0 ? 'none' : shown,
      textAlign: horizontalAlignLevel[3],
      ...calculateWidth(sizes.lg),
      ...calculateOffset(offsets.lg)
    },
    xl: {
      display: hiddenLevels[4] === 0 ? 'none' : shown,
      textAlign: horizontalAlignLevel[4],
      ...calculateWidth(sizes.xl),
      ...calculateOffset(offsets.xl)
    }
  };
  let colStyles;
  let mediaIds;
  if (enableMediaQueryStyleSheet) {
    const mediaQueryStyles = (0, _utils.createMediaQueryStyles)(stylesByViewport);
    const {
      ids,
      styles
    } = _utils.StyleSheet.create({
      col: {
        ...staticStyles,
        ...mediaQueryStyles
      }
    });
    colStyles = styles.col;
    mediaIds = ids.col;
  } else {
    colStyles = {
      ...staticStyles,
      ...stylesByViewport[viewport]
    };
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_utils.BaseView, {
    ref: ref,
    ...viewProps,
    style: colStyles,
    dataSet: mediaIds && {
      media: mediaIds
    },
    children: children
  });
});
Col.displayName = 'Col';

/*
 * We're disabling default props since passing undefined props to
 * the react-flexbox-grid component sets up blank classes that may cause
 * styling issues.
 */
Col.propTypes = {
  /**
   * Specify number of columns within the 'xs' breakpoint range. `0` hides the column.
   *
   * `true` sets the column width automatically;
   * `false` disables the prop
   *
   */
  xs: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, true, false]),
  /**
   * Specify number of columns within the 'sm' breakpoint range. `0` hides the column.
   *
   * `true` sets the column width automatically;
   * `false` disables the prop
   *
   */
  sm: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, true, false]),
  /**
   * Specify number of columns within the 'md' breakpoint range. `0` hides the column.
   *
   * `true` sets the column width automatically;
   * `false` disables the prop
   *
   */
  md: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, true, false]),
  /**
   * Specify number of columns within the 'lg' breakpoint range. `0` hides the column.
   *
   * `true` sets the column width automatically;
   * `false` disables the prop
   *
   */
  lg: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, true, false]),
  /**
   * Specify number of columns after the 'xl' breakpoint. `0` hides the column.
   *
   * `true` sets the column width automatically;
   * `false` disables the prop
   *
   */
  xl: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, true, false]),
  /**
   * Offset the specified number of columns within the 'xs' breakpoint range. `0` hides the column.
   *
   */
  xsOffset: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  /**
   * Offset the specified number of columns within the 'sm' breakpoint range.
   *
   */
  smOffset: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  /**
   * Offset the specified number of columns within the 'md' breakpoint range.
   *
   */
  mdOffset: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  /**
   * Offset the specified number of columns within the 'lg' breakpoint range.
   *
   */
  lgOffset: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  /**
   * Offset the specified number of columns within the 'xl' breakpoint range.
   */
  xlOffset: _propTypes.default.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]),
  /**
   * The columns of the Grid. Will typically be `FlexGrid.Col` components.
   */
  children: _propTypes.default.node.isRequired,
  /**
   *
   * Align content horizontally within the column.
   *
   * Accepts a `PropType.string` following the [responsive prop](#/Layout?id=responsive) structure.
   */
  horizontalAlign: _utils.responsiveProps.getTypeOptionallyByViewport(_propTypes.default.oneOf(['left', 'center', 'right'])),
  /**
   * (web only) Stretches the column to fill vertical space using `display: flex`.
   * In native apps, FlexGrid.Col behaves as if this is always true (as do all `View`s).
   */
  flex: _propTypes.default.bool
};
var _default = exports.default = Col;